import {catchError, from, map, Observable, switchMap, tap} from 'rxjs';
import {AxiosError, AxiosResponse} from 'axios';
import {UIEntitiesRef, updateAllEntities, upsertEntities, upsertEntitiesById} from '@ngneat/elf-entities';
import {setPage, updatePaginationData} from '@ngneat/elf-pagination';
import {GridSortItem} from '@mui/x-data-grid-pro';

import {Pagination} from '@store/common/pagination.model';
import {clientsContractsStore} from '@store/clients/contracts/clients-contracts.store';
import {
  ClientsContract,
  ClientsContractsFilters,
  ClientsContractsPagination,
  ManageClientContract
} from '@store/clients/contracts/clients-contracts.model';
import {getClientsContractsDataSource} from '@store/clients/contracts/clients-contracts.requests';

import APIAxios, {APIRoutes} from '@api/axios.api';
import SnackError from '@utils/error.utils';
import {TableFilter, TableFilterType} from '@store/common/tableFilter.model';
import {extractFilenameFromResponse} from '@utils/response.utils';
import {filesService} from '@store/files';

export class ClientsContractsService {
  store = clientsContractsStore;

  setSort = (sort: GridSortItem) => this.store.update((state) => ({...state, sort}));
  setPagination = (name: string) => (value: number) => this.store.update((state) => ({
    ...state,
    tablePagination: {
      ...state.tablePagination,
      [name]: value,
    },
  }));

  resetFilters = (clientId?: string, clientUnitId?: string) => this.store.update((state) => ({
    ...state,
    filters: {
      search: '',
      tableFilters: ClientsContractsFilters.initTableFilters(clientId, clientUnitId),
      filterExpiredContracts: true
    },
    tablePagination: {
      ...state.tablePagination,
      page: 1
    }
  }));

  setFilters = (name: string) => (value: any) => this.store.update((state) => ({
    ...state,
    filters: {
      ...state.filters,
      [name]: value,
    },
    tablePagination: {
      ...state.tablePagination,
      page: 1
    }
  }));

  getClientUnitContract = (clientOrganisationUnitId: string, search?: string) => {

    const requestFilters = TableFilter.toRequestFilters([{
      type: TableFilterType.CLIENT_ORGANISATION_UNIT,
      values: [{value: clientOrganisationUnitId, label: ''}]
    }]);

    return from(APIAxios({
      ...APIRoutes.GETClientsContracts(),
      params: {
        ...requestFilters,
        searchString: search || undefined,
        page: 1,
        limit: 50,
        sortBy: 'contract.contractName',
        orderBy: 'ASC',
        filterExpiredContracts: true,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ClientsContractsPagination>) => {
          return response.data.data;
        }),
      );
  };


  getClientsContractsCSV() {
    const {filters: {search, tableFilters, filterExpiredContracts}, sort, tablePagination,} = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.GETClientsContractsCSV(),
      params: {
        ...requestFilters,
        searchString: search || undefined,
        page: tablePagination.page,
        limit: tablePagination.limit,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
        filterExpiredContracts: filterExpiredContracts,
        disablePaginate: true,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response) => {
          const filename = extractFilenameFromResponse(response);
          filesService.downloadDocument(response.data, filename || 'contracts.xlsx');
        }),
      );
  }

  getClientsContracts = (): Observable<ClientsContractsPagination> => {
    const {filters: {search, tableFilters, filterExpiredContracts}, sort, tablePagination,} = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.GETClientsContracts(),
      params: {
        ...requestFilters,
        searchString: search || undefined,
        page: tablePagination.page,
        limit: tablePagination.limit,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
        filterExpiredContracts: filterExpiredContracts,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ClientsContractsPagination>) => {
          return response.data;
        }),
        tap((clientsContractsPagination) => {
          const paginationData = Pagination.toPaginationData(clientsContractsPagination, tablePagination.page);

          return this.store.update(
            upsertEntities(clientsContractsPagination.data),
            upsertEntitiesById(clientsContractsPagination.data.map((clientContract) => clientContract.id), {
              updater: (clientContractUi) => clientContractUi,
              creator: (clientContractId) => ({id: clientContractId, selected: false}),
              ref: UIEntitiesRef,
            }),
            updatePaginationData(paginationData),
            setPage(paginationData.currentPage, clientsContractsPagination.data.map((clientsContract) => clientsContract.id)),
            getClientsContractsDataSource.setSuccess(),
          );
        }),
        getClientsContractsDataSource.trackRequestStatus(),
      );
  };

  createClientContract = (clientUnitId: string | number, data: ManageClientContract): Observable<ClientsContract> => {
    return from(APIAxios({
      ...APIRoutes.POSTClientsContract(clientUnitId),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ClientsContract>) => {
          return response.data;
        }),
        switchMap((contract) => this.getClientsContracts().pipe(map(() => contract))),
      );
  };

  updateClientContract(contractId: number | string, data: ManageClientContract) {
    return from(APIAxios({
      ...APIRoutes.PATCHClientsContract(contractId),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ClientsContract>) => {
          return response.data;
        }),
        switchMap((contract) => this.getClientsContracts().pipe(map(() => contract))),
      );
  }

  setSelectedClientsContracts = (clientsContractIds: number[]) => {
    this.store.update(
      updateAllEntities(
        (clientsContractUI) => ({
          ...clientsContractUI,
          selected: clientsContractIds.includes(clientsContractUI.id)
        }),
        {ref: UIEntitiesRef},
      ),
    );
  };

  getContractById(contractId: number | string) {
    return from(APIAxios(APIRoutes.GETClientContract(contractId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ClientsContract>) => {
          return response.data;
        })
      );
  }

  getContract(contractId: string) {
    return from(APIAxios(APIRoutes.GETClientContract(contractId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ClientsContract>) => {
          return response.data;
        })
      );
  }
}

export const clientsContractsService = new ClientsContractsService();
